import React from 'react';
import { Box, Card, CardContent, Typography, Chip } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const ForecastScenarioCards = ({ forecasts, targetName, horizon }) => {
  const { t } = useTranslation();
  const getScenarioColor = (scenario) => {
    const colors = {
      'BASE': '#667eea',
      'UP': '#11998e',
      'DOWN': '#ff6b6b'
    };
    return colors[scenario] || '#888';
  };

  const getScenarioIcon = (scenario) => {
    if (scenario === 'UP') return <TrendingUp />;
    if (scenario === 'DOWN') return <TrendingDown />;
    return <TrendingFlat />;
  };

  const getScenarioLabel = (scenario) => {
    const labels = {
      'BASE': 'Baseline',
      'UP': 'Upside',
      'DOWN': 'Downside'
    };
    return labels[scenario] || scenario;
  };

  // Group by scenario
  const scenarioData = forecasts.reduce((acc, f) => {
    if (!acc[f.scenario]) {
      acc[f.scenario] = f;
    }
    return acc;
  }, {});

  // Order scenarios: BASE, UP, DOWN
  const orderedScenarios = ['BASE', 'UP', 'DOWN']
    .map(s => scenarioData[s])
    .filter(Boolean);

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
        Forecast Scenarios - {targetName} ({horizon})
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {orderedScenarios.map((forecast) => (
          <Card 
            key={forecast.scenario}
            sx={{ 
              width: '100%',
              borderLeft: `6px solid ${getScenarioColor(forecast.scenario)}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateX(4px)',
                boxShadow: 4
              }
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2
              }}>
                {/* Left: Scenario Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      bgcolor: `${getScenarioColor(forecast.scenario)}15`,
                      color: getScenarioColor(forecast.scenario)
                    }}
                  >
                    {getScenarioIcon(forecast.scenario)}
                  </Box>
                  
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {getScenarioLabel(forecast.scenario)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {targetName}
                    </Typography>
                  </Box>
                </Box>

                {/* Center: Forecast Value */}
                <Box sx={{ textAlign: 'center', minWidth: 120 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 10, fontWeight: 600 }}>
                    {t('forecast.actualValue', 'Actual Value')}
                  </Typography>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 800, 
                      color: getScenarioColor(forecast.scenario),
                      mt: 0.5
                    }}
                  >
                    {forecast.mean.toFixed(2)}%
                  </Typography>
                </Box>

                {/* Right: Range & Probability */}
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  {forecast.p10 && forecast.p90 && (
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 10, fontWeight: 600 }}>
                        {t('forecast.rangeP10P90', 'Range (P10-P90)')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5 }}>
                        {forecast.p10.toFixed(2)}% - {forecast.p90.toFixed(2)}%
                      </Typography>
                    </Box>
                  )}
                  
                  <Chip 
                    label={`${(forecast.probability * 100).toFixed(0)}%`}
                    size="medium"
                    sx={{ 
                      bgcolor: getScenarioColor(forecast.scenario),
                      color: 'white',
                      fontWeight: 700,
                      fontSize: 16,
                      height: 36,
                      minWidth: 70
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default ForecastScenarioCards;

